import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormControlLabel,
  Checkbox,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  ViewColumn as ViewColumnIcon,
  Settings as SettingsIcon,
  Preview as PreviewIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import api from '../api';

// Export steps configuration
const EXPORT_STEPS = [
  {
    id: 'filters',
    label: 'Data Filters',
    description: 'Define criteria for data selection',
    icon: <FilterListIcon />
  },
  {
    id: 'fields',
    label: 'Column Selection',
    description: 'Choose fields and languages to include',
    icon: <ViewColumnIcon />
  },
  {
    id: 'settings',
    label: 'Export Configuration',
    description: 'File format and export options',
    icon: <SettingsIcon />
  },
  {
    id: 'preview',
    label: 'Review & Download',
    description: 'Preview data and generate export',
    icon: <PreviewIcon />
  }
];

// Available field categories based on database analysis
const FIELD_CATEGORIES = {
  basic: {
    label: 'Essential Information',
    icon: '📄',
    description: 'Core food identification and basic properties',
    fields: {
      id: { label: 'Food ID', type: 'string', description: 'Unique identifier' },
      name: { label: 'Name', type: 'string', description: 'Food name' },
      description: { label: 'Description', type: 'string', description: 'Food description' },
      category: { label: 'Category Name', type: 'string', description: 'Category name' },
      image: { label: 'Image URL', type: 'string', description: 'Image URL' },
      isCustom: { label: 'Is Custom', type: 'boolean', description: 'User-created food' },
      isLabelFood: { label: 'Is Label Food', type: 'boolean', description: 'From label scanning' },
      isGeneratedWithAi: { label: 'AI Generated', type: 'boolean', description: 'Generated with AI' }
    }
  },
  nutrition: {
    label: 'Nutritional Data',
    icon: '🍎',
    description: 'Nutritional values and health metrics',
    fields: {
      calories: { label: 'Calories', type: 'decimal', description: 'Energy content (kcal)' },
      protein: { label: 'Protein', type: 'decimal', description: 'Protein content (g)' },
      fat: { label: 'Fat', type: 'decimal', description: 'Fat content (g)' },
      carbs: { label: 'Carbohydrates', type: 'decimal', description: 'Carb content (g)' },
      glycemicIndex: { label: 'Glycemic Index', type: 'decimal', description: 'GI value (0-100)' }
    }
  },
  classification: {
    label: 'Categories & Tags',
    icon: '🏷️',
    description: 'Classification, tags, and geographical associations',
    fields: {
      tags: { label: 'Tags', type: 'array', description: 'Associated tags (comma-separated)' },
      countries: { label: 'Countries', type: 'array', description: 'Associated countries' },
      cuisineType: { label: 'Cuisine Type', type: 'enum', description: 'Cuisine classification' },
      sourceUrls: { label: 'Source URLs', type: 'array', description: 'Data sources' }
    }
  },
  metadata: {
    label: 'System Information',
    icon: '📅',
    description: 'Timestamps and system metadata',
    fields: {
      createdAt: { label: 'Created Date', type: 'datetime', description: 'Creation timestamp' },
      updatedAt: { label: 'Updated Date', type: 'datetime', description: 'Last update timestamp' },
      userId: { label: 'Creator ID', type: 'string', description: 'User who created this food' },
      parentFoodId: { label: 'Parent Food ID', type: 'string', description: 'ID of parent food (if variant)' }
    }
  },
  translations: {
    label: 'Translations',
    icon: '🗣️',
    description: 'Names and descriptions in different languages',
    fields: {
      // All 17 languages from the database (excluding english as it's in basic section)
      arabic_name: { label: 'Name (Arabic)', type: 'string', description: 'Arabic name', language: 'arabic' },
      arabic_description: { label: 'Description (Arabic)', type: 'string', description: 'Arabic description', language: 'arabic' },
      chinese_name: { label: 'Name (Chinese)', type: 'string', description: 'Chinese name', language: 'chinese' },
      chinese_description: { label: 'Description (Chinese)', type: 'string', description: 'Chinese description', language: 'chinese' },
      dutch_name: { label: 'Name (Dutch)', type: 'string', description: 'Dutch name', language: 'dutch' },
      dutch_description: { label: 'Description (Dutch)', type: 'string', description: 'Dutch description', language: 'dutch' },
      french_name: { label: 'Name (French)', type: 'string', description: 'French name', language: 'french' },
      french_description: { label: 'Description (French)', type: 'string', description: 'French description', language: 'french' },
      german_name: { label: 'Name (German)', type: 'string', description: 'German name', language: 'german' },
      german_description: { label: 'Description (German)', type: 'string', description: 'German description', language: 'german' },
      hindi_name: { label: 'Name (Hindi)', type: 'string', description: 'Hindi name', language: 'hindi' },
      hindi_description: { label: 'Description (Hindi)', type: 'string', description: 'Hindi description', language: 'hindi' },
      italian_name: { label: 'Name (Italian)', type: 'string', description: 'Italian name', language: 'italian' },
      italian_description: { label: 'Description (Italian)', type: 'string', description: 'Italian description', language: 'italian' },
      japanese_name: { label: 'Name (Japanese)', type: 'string', description: 'Japanese name', language: 'japanese' },
      japanese_description: { label: 'Description (Japanese)', type: 'string', description: 'Japanese description', language: 'japanese' },
      russian_name: { label: 'Name (Russian)', type: 'string', description: 'Russian name', language: 'russian' },
      russian_description: { label: 'Description (Russian)', type: 'string', description: 'Russian description', language: 'russian' },
      spanish_name: { label: 'Name (Spanish)', type: 'string', description: 'Spanish name', language: 'spanish' },
      spanish_description: { label: 'Description (Spanish)', type: 'string', description: 'Spanish description', language: 'spanish' },
      polish_name: { label: 'Name (Polish)', type: 'string', description: 'Polish name', language: 'polish' },
      polish_description: { label: 'Description (Polish)', type: 'string', description: 'Polish description', language: 'polish' },
      portuguese_name: { label: 'Name (Portuguese)', type: 'string', description: 'Portuguese name', language: 'portuguese' },
      portuguese_description: { label: 'Description (Portuguese)', type: 'string', description: 'Portuguese description', language: 'portuguese' },
      korean_name: { label: 'Name (Korean)', type: 'string', description: 'Korean name', language: 'korean' },
      korean_description: { label: 'Description (Korean)', type: 'string', description: 'Korean description', language: 'korean' },
      turkish_name: { label: 'Name (Turkish)', type: 'string', description: 'Turkish name', language: 'turkish' },
      turkish_description: { label: 'Description (Turkish)', type: 'string', description: 'Turkish description', language: 'turkish' },
      urdu_name: { label: 'Name (Urdu)', type: 'string', description: 'Urdu name', language: 'urdu' },
      urdu_description: { label: 'Description (Urdu)', type: 'string', description: 'Urdu description', language: 'urdu' },
      georgian_name: { label: 'Name (Georgian)', type: 'string', description: 'Georgian name', language: 'georgian' },
      georgian_description: { label: 'Description (Georgian)', type: 'string', description: 'Georgian description', language: 'georgian' }
    }
  }
};

// Filter operators based on field types (from database analysis)
const FILTER_OPERATORS = {
  string: [
    { value: 'contains', label: 'Contains', description: 'Field contains the text' },
    { value: 'equals', label: 'Equals', description: 'Exact match' },
    { value: 'starts_with', label: 'Starts with', description: 'Begins with text' },
    { value: 'ends_with', label: 'Ends with', description: 'Ends with text' },
    { value: 'not_contains', label: 'Does not contain', description: 'Excludes text' }
  ],
  decimal: [
    { value: 'equals', label: 'Equals', description: 'Exact number' },
    { value: 'greater_than', label: 'Greater than', description: '> value' },
    { value: 'less_than', label: 'Less than', description: '< value' },
    { value: 'between', label: 'Between', description: 'Range of values' }
  ],
  boolean: [
    { value: 'equals', label: 'Is', description: 'True or False' }
  ],
  array: [
    { value: 'contains_any', label: 'Contains any', description: 'Has any of the values' },
    { value: 'not_contains', label: 'Does not contain', description: 'Excludes values' }
  ],
  enum: [
    { value: 'equals', label: 'Is', description: 'Exact match from list' },
    { value: 'not_equals', label: 'Is not', description: 'Exclude from list' }
  ],
  datetime: [
    { value: 'after', label: 'After', description: 'After date' },
    { value: 'before', label: 'Before', description: 'Before date' },
    { value: 'between', label: 'Between', description: 'Date range' }
  ]
};

const ExportWizard = ({ open, onClose, onExport }) => {
  const [activeStep, setActiveStep] = useState(0);
  // Initial state for reset
  const initialExportData = {
    filters: [],
    fields: new Set(['id', 'name', 'category']), // Default selections
    format: 'csv',
    filename: 'foods_export',
    maxRows: 10000,
    includeHeaders: true
  };

  const [exportData, setExportData] = useState(initialExportData);
  
  const [isLoading, setIsLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [estimatedCount, setEstimatedCount] = useState(0);
  
  // Backend data state
  const [backendData, setBackendData] = useState({
    availableFields: {},
    filterOperators: {},
    categories: {},
    supportedLanguages: [],
    loading: true
  });

  // Load backend field data on component mount
  useEffect(() => {
    const loadBackendData = async () => {
      try {
        setBackendData(prev => ({ ...prev, loading: true }));
        const response = await api.getExportFields();
        
        console.log('Export fields response:', response); // Debug log
        
        if (response.success) {
          const data = response.data;
          console.log('Available filter operators:', data.filterOperators); // Debug log
          
          setBackendData({
            availableFields: data.availableFields || {},
            filterOperators: data.filterOperators || {},
            categories: data.categories || {},
            supportedLanguages: data.supportedLanguages || [],
            loading: false
          });
        } else {
          throw new Error('Failed to load export fields');
        }
      } catch (error) {
        console.error('Error loading backend data:', error);
        // Don't set global error, just log it and use fallback data
        console.warn('Using fallback field data due to backend error');
        setBackendData(prev => ({ ...prev, loading: false }));
      }
    };
    
    if (open) {
      loadBackendData();
    }
  }, [open]);

  // Track if user has interacted with fields
  const [hasSelectedFields, setHasSelectedFields] = useState(false);

  // Step validation
  const isStepValid = (step) => {
    switch (step) {
      case 0: // Filters - allow proceeding with no filters (export all data)
        return true;
      case 1: // Fields
        return exportData.fields.size > 0;
      case 2: // Settings (Languages step removed)
        return exportData.filename.trim() && exportData.format;
      case 3: // Preview
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (isStepValid(activeStep)) {
      setError(null); // Clear any previous errors when moving to next step
      setActiveStep(prev => Math.min(prev + 1, EXPORT_STEPS.length - 1));
    }
  };

  const handleBack = () => {
    setError(null); // Clear any previous errors when going back
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const handleStepClick = (stepIndex) => {
    // Only allow clicking on completed steps or the current step
    // Don't allow jumping ahead to incomplete steps
    if (stepIndex <= activeStep) {
      setError(null); // Clear any previous errors when changing steps
      setActiveStep(stepIndex);
    }
  };

  const updateExportData = (key, value) => {
    setExportData(prev => ({
      ...prev,
      [key]: value
    }));
    setPreviewData(null); // Clear preview when data changes
  };

  // Reset function to restore initial state
  const resetExportWizard = () => {
    setActiveStep(0);
    setExportData({
      filters: [],
      fields: new Set(['id', 'name', 'category']),
      format: 'csv',
      filename: 'foods_export',
      maxRows: 10000,
      includeHeaders: true
    });
    setHasSelectedFields(false); // Reset field selection state
    setIsLoading(false);
    setPreviewData(null);
    setError(null); // Clear any errors
    setEstimatedCount(0);
  };

  // Handle modal close with reset
  const handleClose = () => {
    resetExportWizard();
    onClose();
  };

  // Helper function to filter out empty/invalid filters
  const getValidFilters = (filters) => {
    return filters.filter(filter => {
      // Always include filters with empty operators (is_empty, is_not_empty, is_null, is_not_null)
      const emptyOperators = ['is_empty', 'is_not_empty', 'is_null', 'is_not_null'];
      if (emptyOperators.includes(filter.operator)) {
        return true;
      }
      
      // For other operators, require a value
      if (!filter.value || (typeof filter.value === 'string' && filter.value.trim() === '')) {
        return false;
      }
      
      // For 'between' operator, require both values
      if (filter.operator === 'between' && (!filter.value2 || filter.value2.toString().trim() === '')) {
        return false;
      }
      
      return true;
    });
  };

  // Filter management
  const addFilter = () => {
    const newFilter = {
      id: Date.now(),
      field: 'name',
      operator: 'contains',
      value: '',
      value2: '',
      // Add logical operator only for subsequent filters (not the first one)
      ...(exportData.filters.length > 0 && { logicalOperator: 'AND' })
    };
    updateExportData('filters', [...exportData.filters, newFilter]);
  };

  const updateFilter = (filterId, updates) => {
    const updatedFilters = exportData.filters.map(filter =>
      filter.id === filterId ? { ...filter, ...updates } : filter
    );
    updateExportData('filters', updatedFilters);
  };

  const removeFilter = (filterId) => {
    const updatedFilters = exportData.filters.filter(filter => filter.id !== filterId);
    updateExportData('filters', updatedFilters);
  };

  // Field management
  const toggleField = (fieldId) => {
    const newFields = new Set(exportData.fields);
    if (newFields.has(fieldId)) {
      newFields.delete(fieldId);
    } else {
      newFields.add(fieldId);
    }
    setHasSelectedFields(true); // Mark that user has interacted with fields
    updateExportData('fields', newFields);
  };

  const toggleCategory = (categoryKey) => {
    const categoryFields = Object.keys(FIELD_CATEGORIES[categoryKey].fields);
    const newFields = new Set(exportData.fields);
    const allSelected = categoryFields.every(field => newFields.has(field));
    
    if (allSelected) {
      categoryFields.forEach(field => newFields.delete(field));
    } else {
      categoryFields.forEach(field => newFields.add(field));
    }
    setHasSelectedFields(true); // Mark that user has interacted with fields
    updateExportData('fields', newFields);
  };

  // Generate preview
  const generatePreview = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const validFilters = getValidFilters(exportData.filters);
      const previewConfig = {
        fields: Array.from(exportData.fields),
        languages: [], // Empty array since we don't use separate languages step anymore
        filters: validFilters,
        config: {
          includeHeaders: exportData.includeHeaders
        }
      };

      console.log('Generating preview with config:', previewConfig);
      
      const response = await api.post('/export/preview', previewConfig);
      
      if (response.data?.success) {
        setPreviewData({
          totalRows: response.data.data.totalRows,
          sampleData: response.data.data.sampleData || [],
          columns: response.data.data.columns || []
        });
        setEstimatedCount(response.data.data.totalRows);
      } else {
        throw new Error(response.data?.message || 'Failed to generate preview');
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Preview generation error:', err);
      setError(`Failed to generate preview: ${err.response?.data?.message || err.message}`);
      setIsLoading(false);
    }
  };

  // Export execution
  const handleExport = async () => {
    setIsLoading(true);
    try {
      const validFilters = getValidFilters(exportData.filters);
      const exportConfig = {
        fields: Array.from(exportData.fields),
        languages: [], // Empty array since we don't use separate languages step anymore
        filters: validFilters,
        format: exportData.format,
        config: {
          filename: exportData.filename,
          maxRows: exportData.maxRows,
          includeHeaders: exportData.includeHeaders
        }
      };
      
      await onExport(exportConfig);
      handleClose();
    } catch (err) {
      setError('Export failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderFilterStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        🔍 Data Filters
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Add filters to export only specific foods. Leave empty to export all foods.
      </Typography>
      
      {backendData.loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading filter options...
          </Typography>
        </Box>
      )}

      {exportData.filters.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
          <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" gutterBottom>
            No filters applied - All foods will be exported
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            You can proceed to export all foods, or add filters to export specific foods only.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addFilter}
            sx={{ mt: 2 }}
          >
            Add First Filter
          </Button>
        </Paper>
      ) : (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addFilter}
              size="small"
            >
              Add Filter
            </Button>
          </Box>

          {exportData.filters.map((filter, index) => (
            <Card key={filter.id} sx={{ mb: 2 }}>
              <CardContent>
                {/* Show logical operator selector for subsequent filters */}
                {index > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Logic</InputLabel>
                      <Select
                        value={filter.logicalOperator || 'AND'}
                        onChange={(e) => updateFilter(filter.id, { logicalOperator: e.target.value })}
                        label="Logic"
                      >
                        <MenuItem value="AND">
                          <Tooltip title="Both this filter and the previous filters must match" placement="right">
                            <span>AND</span>
                          </Tooltip>
                        </MenuItem>
                        <MenuItem value="OR">
                          <Tooltip title="Either this filter or the previous filters can match" placement="right">
                            <span>OR</span>
                          </Tooltip>
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                      How should this filter combine with previous filters?
                    </Typography>
                  </Box>
                )}
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Field</InputLabel>
                      <Select
                        value={filter.field}
                        onChange={(e) => updateFilter(filter.id, {
                          field: e.target.value,
                          operator: (backendData.filterOperators[getFieldType(e.target.value)] || FILTER_OPERATORS[getFieldType(e.target.value)])[0]?.value,
                          value: '',
                          value2: ''
                        })}
                        label="Field"
                      >
                        {(() => {
                          // Use backend data if available, otherwise fallback to hardcoded
                          const fieldsSource = backendData.availableFields && Object.keys(backendData.availableFields).length > 0 
                            ? backendData.availableFields 
                            : Object.fromEntries(
                                Object.entries(FIELD_CATEGORIES).flatMap(([categoryKey, category]) =>
                                  Object.entries(category.fields)
                                )
                              );
                          
                          // Filter out measurement category fields
                          const filteredFields = Object.entries(fieldsSource).filter(([fieldKey, field]) => {
                            return field.category !== 'measurement';
                          });
                          
                          // Sort fields to show language fields at the end
                          const sortedFields = filteredFields.sort(([keyA, fieldA], [keyB, fieldB]) => {
                            const isLanguageA = fieldA.language || keyA.includes('_name') || keyA.includes('_description');
                            const isLanguageB = fieldB.language || keyB.includes('_name') || keyB.includes('_description');
                            
                            // Language fields go to the end
                            if (isLanguageA && !isLanguageB) return 1;
                            if (!isLanguageA && isLanguageB) return -1;
                            
                            // Within same type, sort alphabetically
                            return fieldA.label.localeCompare(fieldB.label);
                          });
                          
                          return sortedFields.map(([fieldKey, field]) => (
                            <MenuItem key={fieldKey} value={fieldKey}>
                              {field.label}
                            </MenuItem>
                          ));
                        })()}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Operator</InputLabel>
                      <Select
                        value={filter.operator}
                        onChange={(e) => updateFilter(filter.id, { 
                          operator: e.target.value,
                          value: '',
                          value2: ''
                        })}
                        label="Operator"
                      >
                        {(() => {
                          const fieldType = getFieldType(filter.field);
                          const backendOps = backendData.filterOperators[fieldType];
                          const fallbackOps = FILTER_OPERATORS[fieldType];
                          const operators = backendOps || fallbackOps || [];
                          
                          console.log(`Operators for ${filter.field} (${fieldType}):`, {
                            backendOps,
                            fallbackOps,
                            final: operators
                          });
                          
                          return operators.map(op => (
                          <MenuItem key={op.value} value={op.value}>
                            <Tooltip title={op.description} placement="right">
                              <span>{op.label}</span>
                            </Tooltip>
                          </MenuItem>
                        ));
                        })()}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={5}>
                    {renderFilterInput(filter)}
                  </Grid>
                  
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      onClick={() => removeFilter(filter.id)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          {estimatedCount > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Estimated:</strong> {estimatedCount.toLocaleString()} foods match your filters
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );

  const renderFieldsStep = () => {
    // Use backend data if available, otherwise fallback
    const fieldsData = backendData.availableFields || {};
    const categoriesData = backendData.categories || {};
    
    // Group fields by category from backend data
    const groupedFields = {};
    
    // Process backend fields into categories, excluding measurement category
    Object.entries(fieldsData).forEach(([fieldKey, fieldInfo]) => {
      const category = fieldInfo.category || 'other';
      
      // Skip measurement category fields
      if (category === 'measurement') {
        return;
      }
      
      if (!groupedFields[category]) {
        groupedFields[category] = [];
      }
      groupedFields[category].push({ key: fieldKey, info: fieldInfo });
    });

    // If no backend data, use fallback
    if (Object.keys(groupedFields).length === 0) {
      // Fallback to hardcoded categories
      Object.entries(FIELD_CATEGORIES).forEach(([categoryKey, category]) => {
        groupedFields[categoryKey] = Object.entries(category.fields).map(([fieldKey, fieldInfo]) => ({
          key: fieldKey,
          info: fieldInfo
        }));
      });
    }

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          📊 Select Fields
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Choose which columns to include in your export. Selected: {exportData.fields.size} fields
        </Typography>

        {Object.entries(groupedFields).map(([categoryKey, categoryFields]) => {
          const selectedCount = categoryFields.filter(field => exportData.fields.has(field.key)).length;
          const allSelected = selectedCount === categoryFields.length;
          const someSelected = selectedCount > 0 && selectedCount < categoryFields.length;

          // Get category info from backend or fallback
          const backendCategoryInfo = categoriesData[categoryKey];
          const fallbackCategoryInfo = FIELD_CATEGORIES[categoryKey];
          
          // If backend sends icon names like "InfoIcon", use our proper emoji icons
          let categoryInfo;
          if (backendCategoryInfo && typeof backendCategoryInfo.icon === 'string' && backendCategoryInfo.icon.endsWith('Icon')) {
            // Backend is sending icon names like "InfoIcon", use our fallback with proper emojis
            categoryInfo = fallbackCategoryInfo || {
              label: categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1),
              icon: '📋',
              description: `${categoryKey} fields`
            };
          } else {
            categoryInfo = backendCategoryInfo || fallbackCategoryInfo || {
              label: categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1),
              icon: '📋',
              description: `${categoryKey} fields`
            };
          }

          return (
            <Accordion key={categoryKey} defaultExpanded={categoryKey === 'basic'}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Typography variant="h6">
                    {categoryInfo.icon} {categoryInfo.label}
                  </Typography>
                  <Chip 
                    label={`${selectedCount}/${categoryFields.length}`}
                    size="small"
                    color={allSelected ? 'primary' : someSelected ? 'secondary' : 'default'}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={() => {
                          const newFields = new Set(exportData.fields);
                          if (allSelected) {
                            categoryFields.forEach(field => newFields.delete(field.key));
                          } else {
                            categoryFields.forEach(field => newFields.add(field.key));
                          }
                          setHasSelectedFields(true); // Mark that user has interacted with fields
                          updateExportData('fields', newFields);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label="Select All"
                    onClick={(e) => e.stopPropagation()}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  {categoryInfo.description}
                </Typography>
{(() => {
                  // Check if this is a language translation category
                  const isLanguageCategory = categoryInfo.label.toLowerCase().includes('language') ||
                    categoryInfo.label.toLowerCase().includes('translation');
                  
                  if (isLanguageCategory) {
                    // Special table-like layout for language fields - 4 columns exactly
                    const fieldsPerRow = 4;
                    const rows = [];
                    
                    for (let i = 0; i < categoryFields.length; i += fieldsPerRow) {
                      rows.push(categoryFields.slice(i, i + fieldsPerRow));
                    }
                    
                    return (
                      <Box sx={{ width: '100%' }}>
                        {rows.map((row, rowIndex) => (
                          <Box 
                            key={rowIndex}
                            sx={{ 
                              display: 'flex',
                              width: '100%',
                              gap: 1, // Add gap between cells
                              mb: 1,
                              '& > *': {
                                flex: '1 1 25%', // Exactly 25% width for each column
                                maxWidth: '25%',
                                minWidth: 0,
                              }
                            }}
                          >
                            {row.map((field, colIndex) => (
                              <Box
                                key={field.key}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  minHeight: '70px',
                                  p: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  bgcolor: 'background.paper',
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: 'action.hover',
                                  }
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={exportData.fields.has(field.key)}
                                      onChange={() => toggleField(field.key)}
                                      size="small"
                                      sx={{ p: 0.5 }}
                                    />
                                  }
                                  label={
                                    <Box sx={{ flex: 1, minWidth: 0, ml: 1 }}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: 600,
                                          fontSize: '0.85rem',
                                          lineHeight: 1.2,
                                          mb: 0.5
                                        }}
                                      >
                                        {field.info.label}
                                      </Typography>
                                      {field.info.language && (
                                        <Chip 
                                          label={field.info.language} 
                                          size="small" 
                                          sx={{ 
                                            height: 16, 
                                            fontSize: '0.6rem',
                                            mb: 0.5,
                                            '& .MuiChip-label': { px: 0.5 }
                                          }}
                                          color="primary"
                                          variant="outlined"
                                        />
                                      )}
                                      {field.info.description && (
                                        <Typography 
                                          variant="caption" 
                                          color="text.secondary"
                                          sx={{ 
                                            display: 'block',
                                            fontSize: '0.7rem',
                                            lineHeight: 1.1
                                          }}
                                        >
                                          {field.info.description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ 
                                    alignItems: 'flex-start',
                                    width: '100%',
                                    m: 0,
                                    '& .MuiFormControlLabel-label': {
                                      flex: 1,
                                      minWidth: 0
                                    }
                                  }}
                                />
                              </Box>
                            ))}
                            {/* Fill empty cells if needed */}
                            {Array.from({ length: fieldsPerRow - row.length }).map((_, emptyIndex) => (
                              <Box key={`empty-${rowIndex}-${emptyIndex}`} sx={{ flex: '1 1 25%', maxWidth: '25%', pr: 1 }} />
                            ))}
                          </Box>
                        ))}
                      </Box>
                    );
                  } else {
                    // Original grid layout for non-language categories
                    return (
                      <Grid container spacing={2}>
                        {categoryFields.map(field => (
                          <Grid item xs={12} sm={6} md={4} key={field.key}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={exportData.fields.has(field.key)}
                                  onChange={() => toggleField(field.key)}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: field.info.language ? 600 : 400 }}>
                                    {field.info.label}
                                    {field.info.language && (
                                      <Chip 
                                        label={field.info.language} 
                                        size="small" 
                                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                        color="primary"
                                        variant="outlined"
                                      />
                                    )}
                                  </Typography>
                                  {field.info.description && (
                                    <Typography variant="caption" color="text.secondary">
                                      {field.info.description}
                                    </Typography>
                                  )}
                                  {field.info.field_type && (
                                    <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                                      Type: {field.info.field_type}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    );
                  }
                })()}
              </AccordionDetails>
            </Accordion>
          );
        })}

        {Object.keys(groupedFields).length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Loading field options from backend...
          </Alert>
        )}
      </Box>
    );
  };


  const renderSettingsStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        ⚙️ Export Settings
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Export Format</InputLabel>
            <Select
              value={exportData.format}
              onChange={(e) => updateExportData('format', e.target.value)}
              label="Export Format"
            >
              <MenuItem value="csv">CSV - Comma Separated Values</MenuItem>
              <MenuItem value="excel">Excel - Microsoft Excel (.xlsx)</MenuItem>
              <MenuItem value="json">JSON - JavaScript Object Notation</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Filename"
            value={exportData.filename}
            onChange={(e) => updateExportData('filename', e.target.value)}
            helperText="Filename without extension"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Max Rows"
            value={exportData.maxRows}
            onChange={(e) => updateExportData('maxRows', parseInt(e.target.value))}
            inputProps={{ min: 1, max: 100000 }}
            helperText="Maximum number of rows to export"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={exportData.includeHeaders}
                onChange={(e) => updateExportData('includeHeaders', e.target.checked)}
              />
            }
            label="Include column headers"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderPreviewStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        👀 Preview & Export
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>Export Summary</Typography>
            <Typography variant="body2">
              • {exportData.fields.size} fields selected<br/>
              • {getValidFilters(exportData.filters).length} valid filter(s) applied<br/>
              • Format: {exportData.format.toUpperCase()}<br/>
              • Max rows: {exportData.maxRows.toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Button
            variant="outlined"
            onClick={generatePreview}
            disabled={isLoading}
            fullWidth
            startIcon={isLoading ? <CircularProgress size={20} /> : <PreviewIcon />}
            sx={{ height: 'fit-content' }}
          >
            {isLoading ? 'Generating...' : 'Generate Preview'}
          </Button>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {!previewData && !isLoading && !error && (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
          <PreviewIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" gutterBottom>
            Preview not generated yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Generate Preview" to see a sample of your export data.
          </Typography>
        </Paper>
      )}

      {previewData && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Preview ({previewData.sampleData.length} of {previewData.totalRows.toLocaleString()} rows)
          </Typography>
          <Box sx={{ overflow: 'auto', maxHeight: 400 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {Object.keys(previewData.sampleData[0] || {}).map((key) => (
                    <th
                      key={key}
                      style={{
                        padding: 8,
                        borderBottom: '1px solid #ddd',
                        fontWeight: 'bold',
                        backgroundColor: '#f5f5f5'
                      }}
                    >
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData.sampleData.map((row, index) => (
                  <tr key={index}>
                    {Object.values(row).map((value, cellIndex) => (
                      <td
                        key={cellIndex}
                        style={{
                          padding: 8,
                          borderBottom: '1px solid #eee',
                          maxWidth: 150,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {String(value)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Paper>
      )}
    </Box>
  );

  // Helper functions
  const getFieldType = (fieldKey) => {
    for (const category of Object.values(FIELD_CATEGORIES)) {
      if (category.fields[fieldKey]) {
        return category.fields[fieldKey].type;
      }
    }
    return 'string';
  };

  const renderFilterInput = (filter) => {
    const fieldType = getFieldType(filter.field);
    
    // Handle empty/null operators (disable value input)
    const isEmptyOperator = ['is_empty', 'is_not_empty', 'is_null', 'is_not_null'].includes(filter.operator);
    if (isEmptyOperator) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Value"
            value="No value required"
            disabled
            sx={{ 
              '& .MuiInputBase-input': { 
                color: 'text.secondary',
                fontStyle: 'italic' 
              }
            }}
          />
          <InfoIcon color="action" fontSize="small" />
        </Box>
      );
    }
    
    if (fieldType === 'boolean') {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>Value</InputLabel>
          <Select
            value={filter.value}
            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
            label="Value"
          >
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (fieldType === 'enum') {
      return (
        <TextField
          size="small"
          label="Value"
          type="text"
          value={filter.value}
          onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
          placeholder="Enter value..."
          fullWidth
        />
      );
    }

    if (filter.operator === 'between') {
      return (
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            size="small"
            label="From"
            type={fieldType === 'decimal' ? 'number' : fieldType === 'datetime' ? 'date' : 'text'}
            value={filter.value}
            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
            InputLabelProps={fieldType === 'datetime' ? { shrink: true } : undefined}
          />
          <Typography>to</Typography>
          <TextField
            size="small"
            label="To"
            type={fieldType === 'decimal' ? 'number' : fieldType === 'datetime' ? 'date' : 'text'}
            value={filter.value2 || ''}
            onChange={(e) => updateFilter(filter.id, { value2: e.target.value })}
            InputLabelProps={fieldType === 'datetime' ? { shrink: true } : undefined}
          />
        </Box>
      );
    }

    return (
      <TextField
        fullWidth
        size="small"
        label="Value"
        type={fieldType === 'decimal' ? 'number' : fieldType === 'datetime' ? 'date' : 'text'}
        value={filter.value}
        onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
        InputLabelProps={fieldType === 'datetime' ? { shrink: true } : undefined}
      />
    );
  };

  const stepComponents = [
    renderFilterStep,
    renderFieldsStep,
    renderSettingsStep,
    renderPreviewStep
  ];

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <FileDownloadIcon color="primary" />
          <Typography variant="h6">Export Food Data</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 3 }}>
          {EXPORT_STEPS.map((step, index) => (
            <Step 
              key={step.id}
              completed={index < activeStep}
              onClick={() => handleStepClick(index)}
              sx={{ cursor: 'pointer' }}
            >
              <StepLabel
                icon={
                  index < activeStep ? (
                    <CheckCircleIcon color="primary" />
                  ) : (
                    step.icon
                  )
                }
              >
                <Typography variant="body2" fontWeight="bold">
                  {step.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {step.description}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 400 }}>
          {stepComponents[activeStep]()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'space-between' }}>
        <Button 
          onClick={handleClose} 
          disabled={isLoading}
        >
          Cancel
        </Button>
        
        <Box>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0 || isLoading}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          
          {/* Show Export Now button from Settings step (step 2) onward */}
          {activeStep >= 2 && activeStep < EXPORT_STEPS.length - 1 && (
            <Button
              variant="outlined"
              onClick={handleExport}
              disabled={!isStepValid(1) || !isStepValid(2) || isLoading} // Require Fields and Settings to be valid
              startIcon={isLoading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
              sx={{ mr: 1 }}
            >
              {isLoading ? 'Exporting...' : 'Export Now'}
            </Button>
          )}
          
          {activeStep < EXPORT_STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid(activeStep) || isLoading}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleExport}
              disabled={!previewData || isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
            >
              {isLoading ? 'Exporting...' : 'Export Data'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ExportWizard;